/**
 * Documents Page
 *
 * View and manage documents for a specific CaseFile.
 * Displays document upload at top and document list below.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../lib/firebase';
import { DocumentUpload, DocumentsList } from '../components/documents';

interface CaseFileData {
  caseFileId: string;
  childName: string;
  documentCount?: number;
}

interface GetCaseFileResponse {
  success: boolean;
  caseFile: CaseFileData;
}

export function Documents(): JSX.Element {
  const { caseFileId } = useParams<{ caseFileId: string }>();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const [caseFile, setCaseFile] = useState<CaseFileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchCaseFile = async () => {
      if (!caseFileId) {
        setError('No CaseFile ID provided');
        setLoading(false);
        return;
      }

      try {
        const getCaseFile = httpsCallable<{ caseFileId: string }, GetCaseFileResponse>(
          functions,
          'getCaseFile'
        );
        const result = await getCaseFile({ caseFileId });

        if (result.data.success && result.data.caseFile) {
          setCaseFile(result.data.caseFile);
        } else {
          setError('This care record could not be found.');
        }
      } catch (err: any) {
        console.error('Error fetching CaseFile:', err);
        const rawMessage = err?.message || '';
        if (rawMessage.toLowerCase().includes('not found') || rawMessage.toLowerCase().includes('does not have access')) {
          setError('This care record could not be found. It may have been removed or you may not have access.');
        } else {
          setError('Something went wrong while loading this care record. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCaseFile();
  }, [caseFileId]);

  const handleUploadComplete = () => {
    // Trigger refresh of documents list
    setRefreshKey((prev) => prev + 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-airy">
        <header className="bg-white border-b border-soft-neutral">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
            <div className="h-8 w-48 bg-soft-neutral rounded animate-pulse" />
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="card p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-evergreen border-t-transparent rounded-full mx-auto" />
            <p className="mt-4 text-muted">Loading documents...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !caseFile) {
    return (
      <div className="min-h-screen bg-airy">
        <header className="bg-white border-b border-soft-neutral">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
            <button onClick={signOut} className="btn-ghost btn-sm">
              Sign Out
            </button>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="card p-8 text-center">
            <h2 className="text-lg font-semibold text-ink mb-2">Care Record Not Available</h2>
            <p className="text-muted mb-6">{error || 'This care record could not be found.'}</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Go to Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-airy">
      <header className="bg-white border-b border-soft-neutral">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted">{user?.email}</span>
            <button onClick={signOut} className="btn-ghost btn-sm">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8 space-y-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm">
          <Link to="/" className="text-muted hover:text-ink">
            Dashboard
          </Link>
          <span className="text-muted">/</span>
          <Link to={`/casefile/${caseFileId}`} className="text-muted hover:text-ink">
            {caseFile.childName}
          </Link>
          <span className="text-muted">/</span>
          <span className="text-ink">Documents</span>
        </div>

        {/* Back button and title */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/casefile/${caseFileId}`)}
            className="btn-ghost p-2"
            aria-label="Back to CaseFile"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-2xl font-semibold text-ink">Documents</h2>
            <p className="text-muted">
              {caseFile.childName} &bull;{' '}
              {caseFile.documentCount !== undefined
                ? `${caseFile.documentCount} document${caseFile.documentCount !== 1 ? 's' : ''}`
                : 'Loading...'}
            </p>
          </div>
        </div>

        {/* Document Upload */}
        <DocumentUpload caseFileId={caseFileId!} onUploadComplete={handleUploadComplete} />

        {/* Documents List */}
        <DocumentsList key={refreshKey} caseFileId={caseFileId!} />
      </main>
    </div>
  );
}

export default Documents;
